import type { BoxTruckAssetProfile, BoxTruckVerificationInfo, Equipment, TruckDraft } from '@numeo/types-core'
import { EquipmentTypes } from '@numeo/types-core'
import { parseCargoDimensions } from '@numeo/types-main-web-app'
import { getEmptyTruckData } from 'utils/truckDefaults'
import { BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS } from './format'

export type { TruckDraft }

const text = (value: unknown): string | undefined => {
    const trimmed = typeof value === 'string' ? value.trim() : ''
    return trimmed || undefined
}

const whole = (value: unknown): number | undefined => {
    const parsed = typeof value === 'number' ? value : Number(String(value ?? '').trim())
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : undefined
}

/**
 * The vehicle a driver described about themselves, as a truck the operator can save.
 *
 * Everything here was already collected — the driver types their VIN, truck number, equipment, make, model,
 * year and cargo box when they register, and the operator reads it in Verifications. None of it reached an
 * ASSET, because claiming a driver never created one: the vehicle existed only as prose on the driver's
 * record, and every vehicle document the operator gathered afterwards had nowhere to live.
 *
 * An equipment type outside the Box Truck vocabulary is dropped rather than carried: it is a required field
 * on the form below, and a value no option matches would render as an empty picker that silently refuses to
 * save.
 */
/** Only the vehicle fields, so BOTH shapes that carry them fit: the queue row's self-submitted `info` and the
 *  claimed driver's `registration`, which differ on everything else (notably the document DTO). */
type VehicleFields = Partial<
    Pick<
        BoxTruckVerificationInfo,
        | 'equipmentType'
        | 'truckNumber'
        | 'vinNumber'
        | 'vehicleMake'
        | 'vehicleModel'
        | 'vehicleYear'
        | 'cargoDimensions'
        | 'doorWidth'
        | 'doorHeight'
        | 'maxCargoWeightLbs'
        | 'insuranceExpirationDate'
    >
>

export const truckDraftFromVerification = (info: VehicleFields | undefined): TruckDraft => {
    const vehicleType = BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS.find((option) => option.toLowerCase() === text(info?.equipmentType)?.toLowerCase())
    const cargo = parseCargoDimensions(info?.cargoDimensions)

    const boxTruck: Partial<BoxTruckAssetProfile> = {
        ...(vehicleType ? { vehicleType } : {}),
        ...(whole(info?.maxCargoWeightLbs) ? { payloadLbs: whole(info?.maxCargoWeightLbs) } : {}),
        ...(text(info?.insuranceExpirationDate) ? { insuranceExpirationDate: text(info?.insuranceExpirationDate) } : {}),
        ...(whole(info?.doorWidth) ? { doorWidthIn: whole(info?.doorWidth) } : {}),
        ...(whole(info?.doorHeight) ? { doorHeightIn: whole(info?.doorHeight) } : {}),
        ...(cargo ? { cargoLengthIn: cargo.length, cargoWidthIn: cargo.width, cargoHeightIn: cargo.height } : {}),
    }

    return {
        ...getEmptyTruckData(),
        vin: text(info?.vinNumber) ?? '',
        // "Truck Name" on the form. The driver calls it their truck number; the plate has no counterpart in
        // what they submit, so the operator still types that one.
        mainInfo: text(info?.truckNumber) ?? '',
        truckId: '',
        make: text(info?.vehicleMake) ?? '',
        model: text(info?.vehicleModel) ?? '',
        ...(whole(info?.vehicleYear) ? { year: whole(info?.vehicleYear) } : {}),
        ...(vehicleType ? { equipment: { type: EquipmentTypes.TYPES, values: [vehicleType] } } : {}),
        boxTruck,
    }
}

/**
 * Fill the blanks of a draft the operator filled in by hand from one built out of a fuller record.
 *
 * Needed because the Verifications queue is CROSS-TENANT: for a driver nobody has claimed, it exposes only
 * name, phone, equipment and the carrier numbers — the VIN, truck number, make, model and cargo box the
 * driver submitted are withheld until an operator owns the row. So the modal cannot prefill them, and the
 * complete record only becomes readable the moment the claim goes through.
 *
 * Anything the operator TYPED wins. This only fills what they left empty.
 */
export const fillBlankTruckFields = (typed: TruckDraft, fromRecord: TruckDraft): TruckDraft => {
    const root = { ...fromRecord, ...Object.fromEntries(Object.entries(typed).filter(([, value]) => value !== undefined && value !== '' && value !== null)) }
    return {
        ...root,
        boxTruck: { ...(fromRecord.boxTruck ?? {}), ...(typed.boxTruck ?? {}) },
        equipment: typed.equipment?.values?.length ? typed.equipment : fromRecord.equipment,
    }
}

/** Every value the shared truck forms hand back. Narrower than `unknown`, so a branch cannot quietly
 *  accept something the draft has no field for. */
type TruckDraftFieldValue = string | number | boolean | null | undefined | Equipment | string[]

/** The nested blocks these forms write into. `boxTruck.*` comes from the vehicle card, `fuelConfig.*` from
 *  Basic Information; anything else dotted is a typo, not a field. */
const NESTED_BLOCKS = ['boxTruck', 'fuelConfig'] as const
type NestedBlock = (typeof NESTED_BLOCKS)[number]

const isNestedBlock = (value: string): value is NestedBlock => (NESTED_BLOCKS as readonly string[]).includes(value)

/**
 * One field edit on a draft: a root field, or one inside a block this form is known to write.
 *
 * Copied a level at a time rather than walked over a JSON clone — that clone dropped every `undefined` and
 * could not carry a non-JSON value. Exported so the form and its regression test exercise THIS function
 * rather than two copies that can drift apart while the test stays green.
 *
 * A dotted path naming a block nobody writes is REFUSED rather than applied. Walking it generically meant a
 * typo compiled, created a property the draft has no field for, and rode into the create payload as an
 * invalid column — silently, because nothing downstream had a reason to question it.
 */
export const applyTruckDraftUpdate = (current: TruckDraft, field: string, value: TruckDraftFieldValue): TruckDraft => {
    const [parent, child] = field.split('.')
    if (!child) return { ...current, [parent]: value }
    if (!isNestedBlock(parent)) {
        console.error(`[box-truck] ignored an edit to an unsupported path: ${field}`)
        return current
    }
    return parent === 'boxTruck'
        ? { ...current, boxTruck: { ...current.boxTruck, [child]: value } }
        : { ...current, fuelConfig: { ...current.fuelConfig, [child]: value } }
}

/** Whether the operator has given the form enough to save a vehicle at all. VIN and equipment are what the
 *  truck endpoint itself requires; the rest of the form can be filled in later from Assets. */
export const hasTruckEssentials = (truck: TruckDraft): boolean => Boolean(truck.vin?.trim() && (truck.boxTruck?.vehicleType || truck.equipment?.values?.length))

/** True once the operator has typed anything at all — an untouched form means "no vehicle", not "invalid". */
export const hasAnyTruckInput = (truck: TruckDraft): boolean =>
    Boolean(
        truck.vin?.trim() ||
            truck.mainInfo?.trim() ||
            truck.truckId?.trim() ||
            truck.boxTruck?.vehicleType ||
            truck.equipment?.values?.length ||
            // Ticking what the body is fitted with is starting a truck too. Without these the ticks would be
            // read as "no truck wanted" and dropped without a word, which is the one outcome that teaches an
            // operator the fields do not work.
            truck.boxTruck?.equipmentCapabilities?.length ||
            truck.boxTruck?.dockHeightRamps
    )

/**
 * What to tell an operator whose claim went through but whose truck did not get created.
 *
 * Three different reasons, and they are not interchangeable: a record that could not be READ looks exactly
 * like a record with nothing in it, and conflating them sent an operator to type a VIN their driver had
 * already given us. `null` means there is nothing to say — the truck can be created.
 */
export const truckBlockedReason = (completed: TruckDraft, lookupFailed: boolean): 'lookup-failed' | 'incomplete' | null => {
    if (hasTruckEssentials(completed)) return null
    return lookupFailed ? 'lookup-failed' : 'incomplete'
}
